/**
 * Product Detail Vue Component
 * ------------------------------------------------------------------------------
 * Main template in sections/product__main.liquid
 */

import Cookies from 'js-cookie'
import debounce from 'lodash-es/debounce'
import KeenSlider from 'keen-slider'
import axios from 'axios'
import Vue from 'vue'
import ImageZoom from '@/js/components/image-zoom.vue'
import ReadMore from '@/js/components/read-more.vue'
import VueAccordion from '@/js/components/vue-accordion.vue'
import VueCollapsible from '@/js/components/vue-collapsible.vue'
import VideoWrapper from '@/js/components/video-wrapper.vue'
import Newsletter from '@/js/components/newsletter.js'
import { VTooltip } from 'v-tooltip'
import PinchZoom from 'vue-pinch-zoom'

import { useProduct } from '@/js/composables/product'
import { useCart } from '@/js/composables/cart'
import { useFlyouts } from '@/js/composables/flyouts'
import { removeParameterFromUrl, getUrlWithVariant } from '@/js/utils/helpers'
import { storefrontRequestConfig, STOREFRONT_URL } from '@/js/utils/api'
import { transformStoreLocation, variantStockInLocationRequest, getClosestWarehouse } from '@/js/utils/data'
import { readLocationFromCookies, setLocationFromPlace } from '@/js/utils/location'
import { fetchShippingRates } from '@/js/composables/shipping'

Vue.directive('tooltip', VTooltip)

// https://codesandbox.io/s/github/rcbyr/keen-slider-sandboxes/tree/v6/navigation-controls/thumbnails/vue?file=/src/Slider.vue:2056-2175
function ThumbnailPlugin (main) {
  return (slider) => {
    function removeActive () {
      slider.slides.forEach((slide) => {
        slide.classList.remove('keen-slider__slide--active')
      })
    }

    function addActive (idx) {
      slider.slides[idx].classList.add('keen-slider__slide--active')
    }

    function addClickEvents () {
      slider.slides.forEach((slide, idx) => {
        slide.addEventListener('click', () => {
          main.moveToIdx(idx)
        })
      })
    }

    slider.on('created', () => {
      addActive(slider.track.details.rel)
      if (slider.slides.length < 7) slider.container.nextElementSibling.classList.add('hidden')
      addClickEvents()
      main.on('animationStarted', (main) => {
        removeActive()
        const next = main.animator.targetIdx || 0
        addActive(main.track.absToRel(next))
        slider?.moveToIdx(Math.min(slider.track.details.maxIdx, next))
      })
    })
  }
}

export default {
  components: {
    KeenSlider,
    ImageZoom,
    ReadMore,
    VueAccordion,
    VueCollapsible,
    PinchZoom,
    VideoWrapper,
    Newsletter
  },

  props: {
    product: {
      type: Object,
      required: true
    }
  },

  setup (props) {
    const {
      currentVariant,
      quantity,
      productOptions,
      selectedOptions,
      baseUrl,
      isSale,
      isJustListed,
      isPerfectCondition,
      hasImperfections,
      isBrandNew,
      isBestDeal,
      updateVariant
    } = useProduct(props.product)

    const {
      isLoading: isCartLoading,
      isAddingToCart,
      error: cartError,
      addToCart
    } = useCart()

    const {
      toggleFlyout
    } = useFlyouts()

    return {
      currentVariant,
      quantity,
      productOptions,
      selectedOptions,
      baseUrl,
      isSale,
      isJustListed,
      isPerfectCondition,
      hasImperfections,
      isBrandNew,
      isBestDeal,
      updateVariant,

      isCartLoading,
      isAddingToCart,
      cartError,
      addToCart,

      toggleFlyout
    }
  },

  data () {
    return {
      showMobileTooltip: false,
      currentSlide: 0,
      slider: null,
      nav: null,
      hasArrows: false,
      arrows: '(min-width: 1024px)',
      pagination: '(min-width: 300px)',
      hasPagination: false,
      isThumbnailPopupOpen: false,
      currentThumbnail: null,
      enableAddToCart: false,
      deliveryScenario: 2,
      locationDisplayText: '',
      showLocationInputOnPDP: true,
      pickupWarehouseName: '',
      deliveryPriceText: '',
      shippingOptions: [],
      isDeliveryLoading: false,
      pdpLocationInput: '',
      pdpPlacesSuggestions: [],
      isPdpPlaceSuggestOpen: false,
      isPdpLocationFocused: false,
      storeLocationData: null,
      isCheckLocationInProgress: false
    }
  },

  mounted () {
    // if the URL has variant query string, select that variant
    const queryStringValues = new URLSearchParams(window.location.search)
    if (queryStringValues.has('variant')) {
      this.currentVariant = this.product.variants.find(variant => `${variant.id}` === queryStringValues.get('variant'))
    } else if (this.product.variants.length === 1) {
      this.currentVariant = this.product.variants[0]
    }

    this.$nextTick(() => {
      this.slider = new KeenSlider(this.$refs.main, {
        arrows: true,
        loop: false
      }, [
        slider => {
          slider.on('slideChanged', () => {
            this.currentSlide = slider.track.details.rel
          })
        }
      ]
      )

      // Only show arrows when product has more than 1 image (mobile and desktop)
      const hasMultipleMedia = this.product.media && this.product.media.length > 1
      this.hasArrows = hasMultipleMedia

      if (typeof this.pagination === 'boolean') {
        this.hasPagination = hasMultipleMedia && this.pagination
      } else {
        const paginationMediaQuery = window.matchMedia(this.pagination)
        this.hasPagination = hasMultipleMedia && paginationMediaQuery.matches
        if (hasMultipleMedia) {
          paginationMediaQuery.addEventListener('change', (mq) => {
            this.hasPagination = mq.matches
          })
        }
      }

      this.nav = new KeenSlider(this.$refs.nav, {
        slides: {
          perView: 7,
          spacing: 2
        },
        breakpoints: {
          '(min-width: 768px)': {
            slides: {
              perView: 7.5,
              spacing: 6
            }
          },
          '(min-width: 1280px)': {
            slides: {
              perView: 8.5,
              spacing: 8
            }
          }
        }
      }, [ThumbnailPlugin(this.slider)])

      this.useScrollWheel(this.nav)
    })

    this.$nextTick(() => this.checkLocation())
    document.addEventListener('click', this.handleClickOutside)
    window.addEventListener('location-updated', this.handleLocationUpdated)
  },

  beforeDestroy () {
    document.removeEventListener('click', this.handleClickOutside)
    window.removeEventListener('location-updated', this.handleLocationUpdated)
  },

  watch: {
    currentVariant (value, previousValue) {
      if (!value) {
        removeParameterFromUrl('variant')
        return
      }

      if (value.featured_image && this.product.images.length > 1) {
        this.slider?.moveToIdx(value.featured_image.position - 1)
      }

      const url = getUrlWithVariant(window.location.href, value.id)
      window.history.replaceState({ path: url }, '', url)

      if (previousValue && previousValue.id !== value.id) {
        this.checkLocation()
      }
    },

    pdpLocationInput: debounce(function (value) {
      if (value && this.isPdpLocationFocused) {
        this.performPdpPlaceSearch()
      } else {
        this.pdpPlacesSuggestions = []
      }
    }, 500)
  },

  computed: {
    salePercentage () {
      return Math.round((this.product.price / this.product.compare_at_price) * 100) + '% Off'
    },

    dotHelper () {
      return this.slider ? [...Array(this.slider.track.details.slides.length).keys()] : []
    },

    isLastSlide () {
      if (!this.slider) return false
      return this.currentSlide === this.slider.track.details.maxIdx
    },

    currentState () {
      return Cookies.get('state') || 'NSW' // used to filter products based on the available inventory in a location
    },

    isMobile () {
      return window.matchMedia('(max-width: 1024px').matches
    },

    productsPageUrl () {
      const postCode = Cookies.get('postal_code')
      const base = `${window.location.origin}/collections/all`
      return postCode ? `${base}?postCode=${postCode}` : base
    },

    availabilityStatesText () {
      const states = this.storeLocationData?.variants?.available_on
      if (!states?.length) return ''
      const stateNames = states.join(', ')
      return `This product is only available for sale in ${stateNames}.`
    }
  },

  methods: {
    async handleAddToCart () {
      const properties = {
        _location_availability: Cookies.get('postal_code')
      }
      const { success } = await this.addToCart(this.currentVariant.id, this.quantity, properties)
      if (success) {
        this.toggleFlyout('minicart')
        // TODO: to consider changing the header sticky to composables
        document.getElementById('MainHeader').classList.remove('header-hide')
        document.getElementById('MainHeader').classList.add('header-show')
      }
    },

    triggerMobileTooltip () {
      this.showMobileTooltip = !this.showMobileTooltip
    },

    handleClickOutside (event) {
      const tooltip = document.querySelector('.mobile-tooltip')

      if (tooltip && !tooltip.contains(event.target)) {
        this.showMobileTooltip = false
      }
    },

    openThumbnailPopup (image) {
      this.currentThumbnail = image
      this.isThumbnailPopupOpen = true
    },

    closeThumbnailPopup () {
      this.isThumbnailPopupOpen = false
      this.currentThumbnail = null
    },

    async checkLocation () {
      if (!this.currentVariant || !this.currentVariant.available) {
        return
      }
      if (this.isCheckLocationInProgress) {
        return
      }
      this.isCheckLocationInProgress = true

      const locationData = readLocationFromCookies()
      const variantId = this.currentVariant.id

      const data = variantStockInLocationRequest(this.product.id)
      try {
        const { data: responseData } = await axios.post(STOREFRONT_URL, data, storefrontRequestConfig)
        const product = transformStoreLocation(responseData.data, variantId)
        this.storeLocationData = product

        if (!product?.variants) return

        const availableOn = product.variants.available_on || []
        const isACT = this.currentState === 'ACT'
        const isAvailableInState = isACT
          ? (availableOn.includes('ACT') || availableOn.includes('NSW'))
          : availableOn.includes(this.currentState)

        if (isAvailableInState) {
          this.enableAddToCart = true
        } else {
          this.enableAddToCart = false
        }

        if (!locationData) {
          this.deliveryScenario = 2
          this.showLocationInputOnPDP = true
          this.locationDisplayText = ''
          this.pickupWarehouseName = ''
          this.deliveryPriceText = ''
          this.shippingOptions = []
          return
        }

        this.locationDisplayText = locationData.locationPreference

        if (!isAvailableInState) {
          this.deliveryScenario = 3
          this.showLocationInputOnPDP = false
          this.pickupWarehouseName = ''
          this.deliveryPriceText = ''
          this.shippingOptions = []
          return
        }

        this.deliveryScenario = 1
        this.showLocationInputOnPDP = false

        const warehouse = getClosestWarehouse(
          product.variants.availableStores,
          locationData.state,
          locationData.postalCode
        )
        this.pickupWarehouseName = warehouse?.name || ''

        this.isDeliveryLoading = true
        const rates = await fetchShippingRates(variantId)
        this.isDeliveryLoading = false

        this.shippingOptions = rates || []
        if (rates?.length) {
          const primary = rates[0]
          const amount = parseFloat(primary.amount)
          this.deliveryPriceText = amount === 0 ? 'Free' : `$${amount.toFixed(2)}`
        } else {
          this.deliveryPriceText = ''
        }
      } catch (error) {
        console.warn('Location check error:', error)
        this.deliveryScenario = 2
        this.isDeliveryLoading = false
        this.shippingOptions = []
      } finally {
        this.isCheckLocationInProgress = false
      }
    },

    handleLocationUpdated () {
      this.checkLocation()
    },

    formatShippingAmount (opt) {
      if (!opt) return ''
      const amount = parseFloat(opt.amount)
      if (Number.isNaN(amount)) return ''
      if (amount === 0) return 'Free'
      return `$${amount.toFixed(2)}`
    },

    showPdpLocationInput () {
      this.showLocationInputOnPDP = true
      this.isPdpLocationFocused = true
      this.$nextTick(() => this.$refs.pdpLocationInput?.focus())
    },

    performPdpPlaceSearch () {
      /* eslint-disable no-undef */
      if (typeof google === 'undefined' || !google.maps?.places?.AutocompleteService) return
      const service = new google.maps.places.AutocompleteService()
      const self = this
      service.getPlacePredictions(
        {
          input: this.pdpLocationInput,
          types: ['administrative_area_level_1', 'locality', 'political', 'postal_code'],
          componentRestrictions: { country: 'AU' }
        },
        (predictions, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            self.pdpPlacesSuggestions = predictions || []
            self.isPdpPlaceSuggestOpen = true
          } else {
            self.pdpPlacesSuggestions = []
          }
        }
      )
      /* eslint-enable no-undef */
    },

    handlePdpPlaceSelect (placeId) {
      this.pdpPlacesSuggestions = []
      this.isPdpPlaceSuggestOpen = false
      setLocationFromPlace(placeId, () => {
        this.pdpLocationInput = ''
        this.showLocationInputOnPDP = false
        this.checkLocation()
      })
    },

    handlePdpLocationFocus () {
      this.isPdpLocationFocused = true
    },

    handlePdpLocationBlur () {
      setTimeout(() => {
        this.isPdpLocationFocused = false
      }, 200)
    },

    useScrollWheel (slider) {
      let touchTimeout
      let position
      let wheelActive

      const dispatch = (e, name) => {
        position.x -= e.deltaX
        position.y -= e.deltaY
        slider.container.dispatchEvent(
          new CustomEvent(name, {
            detail: {
              x: position.x,
              y: position.y
            }
          })
        )
      }

      const wheelStart = (e) => {
        position = {
          x: e.pageX,
          y: e.pageY
        }
        dispatch(e, 'ksDragStart')
      }

      const wheel = (e) => {
        dispatch(e, 'ksDrag')
      }

      const wheelEnd = (e) => {
        dispatch(e, 'ksDragEnd')
      }

      const eventWheel = (e) => {
        console.log('eventwheel triggered')
        e.preventDefault()
        if (!wheelActive) {
          wheelStart(e)
          wheelActive = true
        }
        wheel(e)
        clearTimeout(touchTimeout)
        touchTimeout = setTimeout(() => {
          wheelActive = false
          wheelEnd(e)
        }, 50)
      }

      slider.container.addEventListener('wheel', eventWheel, {
        passive: false
      })
    }
  }
}
